import { TableCard } from "./TableCard";
import { TabSelect } from "~/components/TabSelect";
import { useState } from "react";
import { TimeframeSelect } from "~/components/TimeframeSelect";
import { useUser } from "~/modules/user/hooks/useUser";
import { TopTransfersEmptyState } from "./TopTransfersEmptyState";
import { TopTransfersData } from "./types";
import { LoginRequiredModal } from "~/modules/downloads/components/LoginRequiredModal";
import * as Analytics from "./analytics";
import { TopTransfersTable } from "./TopTransfersTable";
import { ColumnSpanConfig } from "~/types";

// // // //

export interface Props {
    title?: string;
    topTransfers: TopTransfersData;
    colSpans?: ColumnSpanConfig;
}

export function TopTransfersCard(props: Props) {
    const {
        title = "Latest Transactions",
        colSpans = {
            sm: 12,
            md: 12,
            lg: 12,
            xl: 6,
            xxl: 6,
        },
    } = props;

    const { isLoggedIn } = useUser();

    const [showLoginModal, setShowLoginModal] = useState(false);

    const [timeframe, setTimeframe] = useState<
        // "one_day" | "seven_day" | "thirty_day"
        "seven_day" | "thirty_day"
    >("seven_day");

    // Fallback to 7 days if no data is available for the selected timeframe
    // const transactions = props.transactions.one_day;
    const transactions =
        props.topTransfers[timeframe] ?? props.topTransfers.seven_day;

    const tabs = [
        {
            key: "0-100k",
            content: { label: "$0 - $100k" },
            data: transactions.under_100k,
            loginRequired: false,
        },
        {
            key: "100k-1m",
            content: { label: "$100k - $1M" },
            data: transactions.under_1m,
            loginRequired: false,
        },
        {
            key: "1m-10m",
            content: { label: "$1M - $10M" },
            data: transactions.under_10m,
            loginRequired: false,
        },
        {
            key: "10m-plus",
            content: { label: "$10M+" },
            data: transactions.under_100m,
            loginRequired: true,
        },
    ];

    const customControls = (
        <div className="w-full flex items-start justify-end">
            <TimeframeSelect
                value={timeframe}
                disabled={!isLoggedIn}
                isLoggedIn={isLoggedIn}
                className="w-full"
                size="sm"
                options={[
                    // { value: "one_day", label: "24h" },
                    {
                        value: "seven_day",
                        label: "7 days",
                        testID: "top-transfers-card--7-days",
                    },
                    {
                        value: "thirty_day",
                        label: "30 days",
                        testID: "top-transfers-card--30-days",
                    },
                ]}
                onChange={(updatedValue) => {
                    setTimeframe(
                        // updatedValue as "one_day" | "seven_day" | "thirty_day",
                        updatedValue as "seven_day" | "thirty_day",
                    );

                    Analytics.trackTopTransfersTimeframeChange({
                        prev: timeframe,
                        next: updatedValue,
                    });
                }}
            />
        </div>
    );

    return (
        <>
            <TableCard
                title={title}
                bordered={false}
                colSpans={colSpans}
                button={customControls}
            >
                <TabSelect
                    tabs={tabs}
                    name="top-transfers-tabs"
                    tabStyle="tab"
                    className="px-2.5 lg:px-0"
                    onTabChange={({ prev, next }) => {
                        // Track tab change analytics
                        Analytics.trackTopTransfersTabChange({
                            prev: prev.key,
                            next: next.key,
                        });
                    }}
                    onTabChangeLoginRequired={({ next }) => {
                        // Show login required modal
                        Analytics.trackTopTransfersLoginCtaOpen(next.key);
                        setShowLoginModal(true);
                    }}
                >
                    {(tab) => {
                        if (tab.data.length === 0) {
                            return (
                                <TopTransfersEmptyState
                                    rangeLabel={tab.content.label}
                                />
                            );
                        }

                        return <TopTransfersTable rows={tab.data} />;
                    }}
                </TabSelect>
            </TableCard>

            <LoginRequiredModal
                allowScroll // NOTE - this is very important. When this is set to false, all the visualizations will resize and performance will crawl for a second.
                show={showLoginModal}
                onHide={() => {
                    setShowLoginModal(false);
                }}
                analytics={{
                    onConfirmed: () => {
                        Analytics.trackTopTransfersLoginCtaConfirmed();
                    },
                    onClose: () => {
                        Analytics.trackTopTransfersLoginCtaClose();
                    },
                }}
            />
        </>
    );
}
